import React from "react";
import { Box, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import CountUp from "react-countup";
import { Swords } from "lucide-react";
import UserAvatar from "../common/UserAvatar";

const firstName = (user) => (user?.full_name || "").split(" ").filter(Boolean)[0] || "—";

const METRICS = [
  { label: "Candidates hired", get: (r) => r.candidates_hired || 0, fmt: (v) => v },
  { label: "Job applications", get: (r) => r.applications_made || 0, fmt: (v) => v },
  { label: "Sign-up rate", get: (r) => r.sign_rate || 0, fmt: (v) => `${v}%` },
];

const Contender = ({ row, color, isMe, reduce }) => (
  <Box sx={{ flex: 1, textAlign: "center" }}>
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <UserAvatar user={row.internal_user} size={72} ring={color} tooltip={false} />
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "7px", mt: "13px" }}>
      <Typography component="span" sx={{ fontSize: 17, fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(150, 30%, 12%)", letterSpacing: "-0.3px" }}>
        {row.internal_user.full_name}
      </Typography>
      {isMe && (
        <Typography
          component="span"
          sx={{ fontSize: 10, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 38%, 17%)", backgroundColor: "hsl(80, 60%, 88%)", px: "6px", py: "1px", borderRadius: "5px" }}
        >
          YOU
        </Typography>
      )}
    </Box>
    <Typography sx={{ fontSize: 42, fontFamily: "Segoe UI Bold, sans-serif, system-ui", color, lineHeight: 1, letterSpacing: "-1px", mt: "16px" }}>
      <CountUp end={row.candidates_hired || 0} duration={reduce ? 0 : 1.1} separator="," />
    </Typography>
    <Typography sx={{ fontSize: 11.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 10%, 46%)", mt: "4px" }}>
      candidates hired
    </Typography>
  </Box>
);

const CompareRow = ({ metric, a, b, reduce }) => {
  const av = metric.get(a);
  const bv = metric.get(b);
  const total = av + bv || 1;
  const ap = (av / total) * 100;
  const aWins = av >= bv;
  return (
    <Box sx={{ py: "11px", borderTop: "1px solid hsl(140, 8%, 91%)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "7px" }}>
        <Typography sx={{ fontSize: 14, fontFamily: aWins ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui", color: aWins ? "hsl(150, 45%, 30%)" : "hsl(150, 10%, 46%)", fontVariantNumeric: "tabular-nums", width: 70, textAlign: "left" }}>
          {metric.fmt(av)}
        </Typography>
        <Typography sx={{ fontSize: 11.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 10%, 46%)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
          {metric.label}
        </Typography>
        <Typography sx={{ fontSize: 14, fontFamily: !aWins ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui", color: !aWins ? "hsl(217, 70%, 52%)" : "hsl(150, 10%, 46%)", fontVariantNumeric: "tabular-nums", width: 70, textAlign: "right" }}>
          {metric.fmt(bv)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", height: 6, borderRadius: "999px", overflow: "hidden", backgroundColor: "hsl(140, 8%, 92%)", gap: "2px" }}>
        <Box
          component={motion.div}
          initial={{ width: reduce ? `${ap}%` : "50%" }}
          animate={{ width: `${ap}%` }}
          transition={reduce ? { duration: 0 } : { duration: 0.85, ease: [0.22, 0.61, 0.36, 1] }}
          sx={{ backgroundColor: "hsl(150, 45%, 30%)", opacity: aWins ? 1 : 0.45, borderRadius: "999px 2px 2px 999px" }}
        />
        <Box sx={{ flex: 1, backgroundColor: "hsl(217, 70%, 52%)", opacity: !aWins ? 1 : 0.45, borderRadius: "2px 999px 999px 2px" }} />
      </Box>
    </Box>
  );
};

const TugOfWar = ({ contenders = [], currentUserId = null, periodLabel = "this month" }) => {
  const reduce = useReducedMotion();
  const [a, b] = contenders;

  if (!a || !b) {
    return (
      <Box sx={{ backgroundColor: "#ffffff", border: "1px solid hsl(140, 8%, 88%)", borderRadius: "8px", p: "28px", textAlign: "center" }}>
        <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150, 10%, 46%)" }}>
          Not enough activity to start a race yet.
        </Typography>
      </Box>
    );
  }

  const gap = (a.candidates_hired || 0) - (b.candidates_hired || 0);

  return (
    <Box
      sx={{
        border: "1px solid hsl(140, 8%, 88%)",
        borderRadius: "8px",
        p: "28px",
        background: "linear-gradient(180deg, hsl(140, 14%, 97%), #fff)",
      }}
    >
      <Box sx={{ textAlign: "center", mb: "22px" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px", color: "hsl(150, 38%, 17%)" }}>
          <Swords size={14} color="hsl(150, 38%, 17%)" strokeWidth={2} />
          <Typography component="span" sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", letterSpacing: "0.8px", textTransform: "uppercase" }}>
            Race for #1 · {periodLabel}
          </Typography>
        </Box>
        <Typography sx={{ m: "8px 0 0", fontSize: 13.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
          {firstName(a.internal_user)} leads{" "}
          {firstName(b.internal_user)} by {gap} hire{gap === 1 ? "" : "s"}.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px", maxWidth: 640, mx: "auto" }}>
        <Contender row={a} color="hsl(150, 45%, 30%)" isMe={currentUserId === a.internal_user.id} reduce={reduce} />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: "30px" }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              backgroundColor: "#ffffff",
              border: "1px solid hsl(140, 8%, 88%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              color: "hsl(150, 18%, 28%)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            VS
          </Box>
        </Box>
        <Contender row={b} color="hsl(217, 70%, 52%)" isMe={currentUserId === b.internal_user.id} reduce={reduce} />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", mt: "24px" }}>
        {METRICS.map((metric) => (
          <CompareRow key={metric.label} metric={metric} a={a} b={b} reduce={reduce} />
        ))}
      </Box>
    </Box>
  );
};

export default TugOfWar;
